/**
 * Хранилище черновика: история для отмены/повтора (с объединением ввода),
 * подписчики и автосохранение в localStorage.
 */
import type { Draft, EditTarget } from '../../src/shared/types.js';
import { newDraft, normalizeDraft } from '../../src/shared/factory.js';

type Listener = (kind: 'content' | 'structure') => void;

const HISTORY_LIMIT = 150;
const COALESCE_MS = 900;

function safeStorage(): Storage | null {
  try {
    const s = window.localStorage;
    s.getItem('x');
    return s;
  } catch {
    return null;
  }
}

export class DraftStore {
  draft: Draft = newDraft();
  /** правка существующего сообщения */
  editTarget: EditTarget | null = null;
  /** шаблон, из которого загружен черновик */
  templateId: string | null = null;
  templateName: string | null = null;

  private past: string[] = [];
  private future: string[] = [];
  private lastKey = '';
  private lastTime = 0;
  private listeners = new Set<Listener>();
  private storageKey = '';
  private saveTimer: ReturnType<typeof setTimeout> | undefined;

  subscribe(fn: Listener) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(kind: 'content' | 'structure') {
    for (const l of this.listeners) l(kind);
    this.scheduleSave();
  }

  /** Привязка к серверу: восстановление автосохранения. */
  attach(guildId: string) {
    this.storageKey = `es:draft:${guildId}`;
    this.past = [];
    this.future = [];
    const raw = safeStorage()?.getItem(this.storageKey);
    if (raw) {
      try {
        const saved = JSON.parse(raw);
        this.draft = normalizeDraft(saved.draft);
        this.editTarget = saved.editTarget ?? null;
        this.templateId = saved.templateId ?? null;
        this.templateName = saved.templateName ?? null;
        return true;
      } catch {
        /* битое автосохранение */
      }
    }
    this.draft = newDraft();
    this.editTarget = null;
    this.templateId = null;
    this.templateName = null;
    return false;
  }

  private scheduleSave() {
    if (!this.storageKey) return;
    clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      safeStorage()?.setItem(
        this.storageKey,
        JSON.stringify({ draft: this.draft, editTarget: this.editTarget, templateId: this.templateId, templateName: this.templateName }),
      );
    }, 400);
  }

  private snapshot(): string {
    return JSON.stringify(this.draft);
  }

  private pushHistory() {
    this.past.push(this.snapshot());
    if (this.past.length > HISTORY_LIMIT) this.past.shift();
    this.future = [];
  }

  /**
   * Перед изменением поля при вводе текста. Изменения одного поля подряд
   * объединяются в один шаг истории.
   */
  touch(key: string) {
    const now = Date.now();
    if (key !== this.lastKey || now - this.lastTime > COALESCE_MS) this.pushHistory();
    this.lastKey = key;
    this.lastTime = now;
  }

  /** Изменение значения поля (текст, чекбокс и т.п.). */
  input(key: string, apply: () => void) {
    this.touch(key);
    apply();
    this.emit('content');
  }

  /** Структурное изменение: добавление, удаление, перемещение. */
  change(apply: (d: Draft) => void) {
    this.pushHistory();
    this.lastKey = '';
    apply(this.draft);
    this.emit('structure');
  }

  /** Полная замена черновика (импорт, шаблон, очистка). */
  replace(d: Draft, meta: { editTarget?: EditTarget | null; templateId?: string | null; templateName?: string | null } = {}) {
    this.pushHistory();
    this.lastKey = '';
    this.draft = normalizeDraft(d);
    if ('editTarget' in meta) this.editTarget = meta.editTarget ?? null;
    if ('templateId' in meta) this.templateId = meta.templateId ?? null;
    if ('templateName' in meta) this.templateName = meta.templateName ?? null;
    this.emit('structure');
  }

  setMeta(meta: { editTarget?: EditTarget | null; templateId?: string | null; templateName?: string | null }) {
    if ('editTarget' in meta) this.editTarget = meta.editTarget ?? null;
    if ('templateId' in meta) this.templateId = meta.templateId ?? null;
    if ('templateName' in meta) this.templateName = meta.templateName ?? null;
    this.emit('structure');
  }

  canUndo() {
    return this.past.length > 0;
  }
  canRedo() {
    return this.future.length > 0;
  }

  undo() {
    const prev = this.past.pop();
    if (prev === undefined) return;
    this.future.push(this.snapshot());
    this.draft = JSON.parse(prev);
    this.lastKey = '';
    this.emit('structure');
  }

  redo() {
    const next = this.future.pop();
    if (next === undefined) return;
    this.past.push(this.snapshot());
    this.draft = JSON.parse(next);
    this.lastKey = '';
    this.emit('structure');
  }
}

export const store = new DraftStore();
